import { useSelector } from 'react-redux';
import { useQuery } from 'react-apollo';

import { EventAddonItems } from '../../../../../graphql/queries/events';
import {
  AlbumFolderForPurchasedEventAddonFeatureContributors,
  AlbumForPurchasedEventAddonFeatureContributors,
} from './graphql/queries';
import {
  getAddonForFeature,
  isAddonFeatureEnabled,
  isAddonFeatureEnabledForAddons,
} from './helpers';
import { AccountForSearchSubscriptionFeaturesQuery } from '../../searchSubscriptionFeature/hooks/graphql/queries';
import { PURCHASED_EVENT_ADDON_FEATURES } from '../../featurePermissions/helpers';

/**
 * Determines whether a purchased event add on feature is available  for a certain
 * hierarchy item. By default, this will return true for all items outside of those
 * with album_type = EVENT and album_subtype in [BIRTHDAY, FUNDRAISERS, MEMORIALS...]
 *
 * Do not use as is.
 *
 * @see usePurchasedEventAddonPermission
 *
 * @param {Object} options The option list.
 * @param {String} options.albumId The album id
 * @param {String} options.albumFolderId The album folder id
 * @param {Array<String>} options.features List of Ids of the feature to check for
 * @param {Array<String>} options.eventAddonIds List of eventAddonIds to rely on for feature access
 * @param {Object} options.eventAddonItemsQueryVars Object of variables for the eventAddonItemsQuery
 * @param {Boolean} options.ownerOnly Whether to run queries for owners only
 * @returns {Object}
 */
const usePurchasedEventAddonFeature = ({
  albumId,
  albumFolderId,
  features = [],
  eventAddonIds = [],
  eventAddonItemsQueryVars,
  ownerOnly = false,
} = {}) => {
  const authenticated = useSelector(({ auth }) => auth.authenticated);
  const { loading: loadingAlbum, data: { album } = {} } = useQuery(
    AlbumForPurchasedEventAddonFeatureContributors,
    {
      variables: {
        id: albumId,
      },
      notifyOnNetworkStatusChange: true,
      skip: !authenticated || !albumId,
    },
  );
  const { loading: loadingAlbumFolder, data: { albumFolder } = {} } = useQuery(
    AlbumFolderForPurchasedEventAddonFeatureContributors,
    {
      variables: {
        id: albumFolderId,
      },
      notifyOnNetworkStatusChange: true,
      skip: !authenticated || !albumFolderId,
    },
  );

  const isOwner = album?.is_owner || albumFolder?.is_owner;

  const {
    loading: loadingEventAddonItems,
    data: { eventAddonItems } = {},
  } = useQuery(EventAddonItems, {
    variables: eventAddonItemsQueryVars || undefined,
    skip: ownerOnly && !album?.is_owner && !albumFolder?.is_owner,
  });
  const { loading: loadingAccount, data: { me: account } = {} } = useQuery(
    AccountForSearchSubscriptionFeaturesQuery,
    {
      skip: !authenticated || (ownerOnly && !isOwner),
      notifyOnNetworkStatusChange: true,
    },
  );

  const hierarchyItem = album ?? albumFolder;
  const loading =
    loadingAccount ||
    loadingEventAddonItems ||
    loadingAlbum ||
    loadingAlbumFolder;

  if (
    // While loading, we'll always assume things are not allowed to avoid
    // any loophole.
    loading
  ) {
    return features.reduce(
      (acc, featureId) => {
        return {
          ...acc,
          [featureId]: {
            enabled: false,
          },
        };
      },
      { id: null, loading },
    );
  }

  if (eventAddonIds?.length > 0) {
    const eventAddons = eventAddonItems.filter(({ id }) =>
      eventAddonIds.includes(id),
    );

    return features.reduce(
      (acc, featureId) => {
        return {
          ...acc,
          [featureId]: {
            enabled: isAddonFeatureEnabledForAddons(eventAddons, featureId),
            addon: getAddonForFeature(eventAddonItems, featureId),
          },
        };
      },
      // Create a pseudoId just in case this is used during FreemiumEventCreation.
      // since we don't have either album nor folder at  that point, doing this ensures
      // the id is updated and we can rely on it to update stepProps
      { id: album?.id ?? albumFolder?.id ?? eventAddonIds.join(',') },
    );
  }

  return features.reduce(
    (acc, featureId) => {
      return {
        ...acc,
        [featureId]: {
          enabled:
            // QR Code is now tied to each event individually, so we will not
            // rely on the account permissions, which will always be true, for
            // this scenario.
            (PURCHASED_EVENT_ADDON_FEATURES.qr_code_enabled !== featureId &&
              !!account?.active_search_subscription_features?.[featureId]) ||
            isAddonFeatureEnabled(
              hierarchyItem,
              !!account?.active_search_subscription?.id,
              featureId,
            ),
          addon: getAddonForFeature(eventAddonItems, featureId),
        },
      };
    },
    { id: album?.id ?? albumFolder?.id },
  );
};

export default usePurchasedEventAddonFeature;
